/**
 * Ground distance and area for a sketched shape.
 *
 * Geodesic, not planar. Measuring in degrees — or in Web Mercator metres —
 * looks right at the equator and is wrong everywhere else: a Mercator metre at
 * 60° north covers half the ground a real one does, so a planar reading of a
 * Scandinavian pipeline would be out by a factor of two. These work on the
 * sphere, so a length is the length someone would walk.
 *
 * The sphere, not the WGS84 ellipsoid: the difference is under 0.5%, which is
 * far inside the error of a shape drawn by clicking on a screen.
 */

/** Mean Earth radius, in metres. */
const R = 6371008.8;

const toRad = (degrees: number) => (degrees * Math.PI) / 180;

/** Great-circle distance between two [lng, lat] positions, in metres. */
export function distanceBetween(a: [number, number], b: [number, number]): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Total length along a run of positions, in metres. */
export function lineLength(coordinates: [number, number][]): number {
  let total = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    total += distanceBetween(coordinates[index - 1], coordinates[index]);
  }
  return total;
}

/**
 * Area of a closed ring, in square metres.
 *
 * The spherical excess formula every GIS uses for this — the signed sum of
 * longitude spans weighted by latitude — with the sign dropped, since a ring
 * wound the other way encloses the same ground.
 */
export function ringArea(ring: [number, number][]): number {
  if (ring.length < 3) return 0;
  let total = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const [lng1, lat1] = ring[index];
    const [lng2, lat2] = ring[(index + 1) % ring.length];
    total += toRad(lng2 - lng1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((total * R * R) / 2);
}

/** Length or area of a finished sketch, in metres or square metres. */
export function measureGeometry(geometry: GeoJSON.Geometry): {
  kind: 'Distance' | 'Area';
  metres: number;
} | null {
  if (geometry.type === 'LineString') {
    return { kind: 'Distance', metres: lineLength(geometry.coordinates as [number, number][]) };
  }
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates as [number, number][][];
    if (rings.length === 0) return null;
    // Outer ring minus any holes, which is what the shape actually covers.
    const area = rings.reduce(
      (total, ring, index) => (index === 0 ? ringArea(ring) : total - ringArea(ring)),
      0,
    );
    return { kind: 'Area', metres: Math.max(0, area) };
  }
  return null;
}

/**
 * Writes a length the way a person reads one.
 *
 * Steps up to the larger unit past a threshold rather than at exactly 1, so a
 * reading never flickers between "999 m" and "1.00 km" as the cursor moves.
 */
export function formatLength(metres: number, unit: 'metric' | 'imperial'): string {
  if (unit === 'imperial') {
    const feet = metres / 0.3048;
    if (feet < 1000) return `${feet.toFixed(feet < 10 ? 2 : 0)} ft`;
    return `${(feet / 5280).toFixed(2)} mi`;
  }
  if (metres < 1000) return `${metres.toFixed(metres < 10 ? 2 : 0)} m`;
  return `${(metres / 1000).toFixed(2)} km`;
}

export function formatArea(squareMetres: number, unit: 'sqm' | 'hectare' | 'acre'): string {
  if (unit === 'hectare') return `${(squareMetres / 10_000).toFixed(2)} ha`;
  if (unit === 'acre') return `${(squareMetres / 4046.8564224).toFixed(2)} ac`;
  if (squareMetres < 1_000_000) {
    return `${squareMetres.toLocaleString(undefined, { maximumFractionDigits: 0 })} m²`;
  }
  return `${(squareMetres / 1_000_000).toFixed(2)} km²`;
}

/**
 * A circle on the ground, as a polygon ring.
 *
 * Drawn in real metres rather than a fixed degree offset, so a 500 m radius is
 * 500 m at every latitude — a degree of longitude is 111 km at the equator and
 * 56 km at 60°, and a circle built from degrees would be an ellipse squashed
 * by exactly that factor.
 */
export function geodesicCircle(
  centre: { lat: number; lng: number },
  radiusMetres: number,
  segments = 72,
): number[][] {
  const latRad = (centre.lat * Math.PI) / 180;
  const dLat = (radiusMetres / R) * (180 / Math.PI);
  const dLng = dLat / Math.max(Math.cos(latRad), 1e-6);
  const ring: number[][] = [];
  for (let step = 0; step <= segments; step += 1) {
    const angle = (step / segments) * 2 * Math.PI;
    ring.push([centre.lng + dLng * Math.cos(angle), centre.lat + dLat * Math.sin(angle)]);
  }
  return ring;
}
